import styled from 'styled-components';

// The sing-along switch (issues #3518, #3722), lifted out of Pitch Match's start screen so Sing in
// Solfège's start screen draws the same one. Each module stores its own choice per device: Pitch
// Match's (pitch_mode.ts) is on by default where the echo canceller lets the voice through, and Sing
// in Solfège's is off until the singer turns it on (sing_in_solfege_page.tsx). Sing-along keeps the
// piano sounding while the singer sings; the phones whose voice processing mutes the singer while
// it does need it off, which is what the note under the switch is for. The whole row is the switch,
// so the label is both the tap target and the accessible name. Each screen passes its own id, and
// may pass its own label and note: Pitch Match's sentences are about its one note and a switch that
// starts on, and are the default; Sing in Solfège's say "the guide", since what keeps playing there
// is the whole phrase, and its note is written for a switch that starts off (#3722).

export const singAlongLabel = 'Keep playing the note while I sing';
export const singAlongGuideLabel = 'Keep playing the guide while I sing';
export const singAlongNote = 'Turn this off if the note stops your phone from hearing you.';
export const singAlongGuideNote = 'Plays the phrase softly under you. Some phones stop hearing you while it plays.';

export const SingAlongSwitch = ({
  id,
  label = singAlongLabel,
  note = singAlongNote,
  singAlong,
  onToggleSingAlong,
}: {
  id: string;
  label?: string; // what the switch says; Pitch Match's sentence when absent
  note?: string; // the line under it; Pitch Match's when absent
  singAlong: boolean; // the device's stored choice, or its default
  onToggleSingAlong: (singAlong: boolean) => void; // the value the switch was moved to
}) => (
  <SingAlongRow>
    <SwitchButton
      id={id}
      data-testid={id}
      type="button"
      role="switch"
      aria-checked={singAlong}
      data-on={singAlong ? 'true' : 'false'}
      onClick={() => onToggleSingAlong(!singAlong)}
    >
      <SwitchLabel>{label}</SwitchLabel>
      <SwitchTrack $on={singAlong}>
        <SwitchKnob $on={singAlong} />
      </SwitchTrack>
    </SwitchButton>
    <SwitchNote>{note}</SwitchNote>
  </SingAlongRow>
);

const SingAlongRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 28px;
`;

const SwitchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

const SwitchLabel = styled.span`
  font-size: 15px;
  line-height: 22px;
`;

const switchWidth = 50;
const switchHeight = 30;
const knobSize = 24;

const SwitchTrack = styled.span<{ $on: boolean }>`
  flex: none;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: ${switchWidth}px;
  height: ${switchHeight}px;
  padding: 0 3px;
  border-radius: ${switchHeight / 2}px;
  border: 2px solid ${({ $on }) => ($on ? 'var(--color-tab-selected)' : 'var(--color-divider)')};
  background-color: ${({ $on }) => ($on ? 'var(--color-tab-selected)' : 'transparent')};
  transition: background-color 120ms linear;
`;

const SwitchKnob = styled.span<{ $on: boolean }>`
  width: ${knobSize}px;
  height: ${knobSize}px;
  border-radius: 50%;
  background-color: ${({ $on }) => ($on ? 'var(--color-background)' : 'var(--color-divider)')};
  transform: translateX(${({ $on }) => ($on ? switchWidth - knobSize - 10 : 0)}px);
  transition: transform 120ms linear;
`;

const SwitchNote = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: var(--color-text-darker);
`;
